

import { useEffect, useState } from "react";
import { useRouter } from '@/lib/navigation';
import { Download, Smartphone, CheckCircle, ArrowRight, Sparkles, Shield } from "lucide-react";
import { usePWA } from "@/hooks/usePWA";
import { useSettings } from "@/context/SettingsContext";
import { getImageUrl } from "@/lib/utils";

export default function InstallPage() {
  const router = useRouter();
  const { isInstallable, isStandalone, promptInstall, isIOS } = usePWA();
  const { settings } = useSettings();
  const { businessName, tagline } = settings.business;
  const { logoUrl, primaryColor } = settings.theme;

  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);

  // Get app name from settings or default
  const appName = businessName || "Shalimar ERP";
  const appTagline = tagline || "Premium Event Management ERP";
  const appLogo = logoUrl ? getImageUrl(logoUrl) : "/icon-192x192.png";
  const themeColor = primaryColor || "#C9A84C";

  // If already in standalone mode (installed), redirect to login
  useEffect(() => {
    if (isStandalone) {
      router.push("/login");
    }
  }, [isStandalone, router]);

  const handleInstall = async () => {
    setInstalling(true);
    try {
      await promptInstall();
      setInstalled(true);
      // Redirect to login after successful install
      setTimeout(() => {
        router.push("/login");
      }, 2000);
    } catch (error) {
      console.error("Install failed:", error);
    } finally {
      setInstalling(false);
    }
  };

  const features = [
    { icon: Smartphone, title: "Home Screen Access", desc: "Launch directly from your home screen" },
    { icon: Shield, title: "Secure & Fast", desc: "Native app-like experience" },
    { icon: Sparkles, title: "Works Offline", desc: "Access key features without internet" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-stone-100 flex flex-col">
      {/* Header */}
      <header className="p-4 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: themeColor }}
          >
            <img src={appLogo} alt={appName} className="w-6 h-6 object-contain" />
          </div>
          <span className="font-semibold text-stone-800">{appName}</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* App Icon & Info */}
          <div className="text-center mb-8">
            <div className="relative inline-block mb-6">
              <div
                className="w-28 h-28 rounded-3xl flex items-center justify-center shadow-2xl mx-auto"
                style={{ backgroundColor: themeColor }}
              >
                <img src={appLogo} alt={appName} className="w-20 h-20 object-contain" />
              </div>
              <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-stone-800 mb-2">{appName}</h1>
            <p className="text-stone-500">{appTagline}</p>
          </div>

          {/* Features */}
          <div className="space-y-3 mb-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-100 shadow-sm"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${themeColor}20` }}
                >
                  <feature.icon className="w-6 h-6" style={{ color: themeColor }} />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800">{feature.title}</h3>
                  <p className="text-sm text-stone-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Install Button or Status */}
          <div className="space-y-3">
            {installed ? (
              <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-center">
                <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="font-semibold text-emerald-800">App Installed!</p>
                <p className="text-sm text-emerald-600">Redirecting to login...</p>
              </div>
            ) : isInstallable ? (
              <button
                onClick={handleInstall}
                disabled={installing}
                className="w-full py-4 rounded-xl text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                style={{ backgroundColor: themeColor }}
              >
                {installing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Installing...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Install App
                  </>
                )}
              </button>
            ) : isIOS ? (
              <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
                <p className="font-semibold text-amber-800 mb-2">Install on iPhone/iPad</p>
                <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                  <li>Tap the Share button in Safari</li>
                  <li>Scroll down and tap "Add to Home Screen"</li>
                  <li>Tap "Add" to confirm</li>
                </ol>
              </div>
            ) : (
              <div className="p-4 bg-stone-100 rounded-xl text-center">
                <p className="text-stone-600">
                  Open this page in your browser&apos;s menu and select &quot;Install App&quot; or &quot;Add to Home Screen&quot;
                </p>
              </div>
            )}
          </div>

          {/* iOS Instructions Note */}
          {isIOS && (
            <p className="text-center text-xs text-stone-400 mt-6">
              iOS requires manual installation. Use the Share button in Safari.
            </p>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center">
        <p className="text-xs text-stone-400">
          By installing, you agree to our Terms of Service and Privacy Policy
        </p>
      </footer>
    </div>
  );
}
